import React, { useEffect, useMemo } from 'react';
import {
  Row,
  Col,
  DatePicker,
  InputNumber,
  Button,
  Space,
  Collapse,
  Typography,
  theme,
} from 'antd';
import { useIntl } from 'react-intl';
import { Dayjs } from 'dayjs';
import { SearchOutlined, CaretRightOutlined } from '@ant-design/icons';

import useIsMobile from '@/hooks/useIsMobile';
import NightsStepper from './NightsStepper';

const { RangePicker } = DatePicker;
const { Panel } = Collapse;

interface BookingSearchFormVersion2Props {
  dates: [Dayjs | null, Dayjs | null] | null;
  minSelectableDate: Dayjs;
  adults: number;
  children: number;
  infants: number;
  onDatesChange: (vals: [Dayjs | null, Dayjs | null] | null) => void;
  onAdultsChange: (val: number) => void;
  onChildrenChange: (val: number) => void;
  onInfantsChange: (val: number) => void;
  onTrigger: () => void;
  loading?: boolean;
}

const DATE_FORMAT = 'DD-MM-YYYY';
const MAX_NIGHTS = 30;
// Locales whose guest-label text (e.g. "Infant / Room") is long enough to overflow
// the addon and overlap the search button — add more locale codes here if needed.
const COMPACT_LABEL_LOCALES = ['ru'];

const BookingSearchFormVersion2: React.FC<BookingSearchFormVersion2Props> = ({
  dates,
  minSelectableDate,
  adults,
  children,
  infants,
  onDatesChange,
  onAdultsChange,
  onChildrenChange,
  onInfantsChange,
  onTrigger,
  loading,
}) => {
  const intl = useIntl();
  const isMobile = useIsMobile(768);

  const { token } = theme.useToken()

  const isCompactLabel = COMPACT_LABEL_LOCALES.includes(intl.locale);

  const disabledDate = (current: Dayjs) => {
    return current && current < minSelectableDate;
  };

  const safeDates = useMemo<[Dayjs, Dayjs]>(() => {
    const rawCheckIn = dates?.[0] ?? minSelectableDate;
    const checkIn = rawCheckIn.isBefore(minSelectableDate, 'day') ? minSelectableDate : rawCheckIn;
    const checkOut = dates?.[1] ?? checkIn.add(1, 'day');

    if (checkOut.isAfter(checkIn, 'day')) {
      return [checkIn, checkOut];
    }

    return [checkIn, checkIn.add(1, 'day')];
  }, [dates, minSelectableDate]);

  const [checkInDate, checkOutDate] = safeDates;

  // If the stored dates fall before minSelectableDate (e.g. the cut-off passed
  // while checkIn=today was still selected), push the clamped dates back so the
  // picker's displayed value and the actual search state stay in sync — otherwise
  // triggering a search re-reads the stale, still-blocked checkIn.
  useEffect(() => {
    const currentCheckIn = dates?.[0];
    const needsCorrection = !currentCheckIn || currentCheckIn.isBefore(minSelectableDate, 'day');
    if (needsCorrection) {
      onDatesChange([checkInDate, checkOutDate]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates, minSelectableDate]);

  const totalNights = useMemo(() => {
    const diff = checkOutDate.startOf('day').diff(checkInDate.startOf('day'), 'day');
    return Math.max(1, diff);
  }, [checkInDate, checkOutDate]);

  const guestSummary = (
    <span style={{ fontWeight: 500 }}>
      {adults} {intl.formatMessage({ id: 'booking.adults', defaultMessage: 'Adults' })},{' '}
      {children}{' '}
      {intl.formatMessage({ id: 'booking.children', defaultMessage: 'Children' })},{' '}
      {infants} {intl.formatMessage({ id: 'booking.infant', defaultMessage: 'Infant' })}
    </span>
  );

  const handleRangeChange = (vals: null | [Dayjs | null, Dayjs | null]) => {
    if (vals && vals[0] && vals[1]) {
      const nextCheckIn = vals[0].startOf('day');
      const nextCheckOut = vals[1].startOf('day');

      if (!nextCheckOut.isAfter(nextCheckIn, 'day')) {
        onDatesChange([nextCheckIn, nextCheckIn.add(1, 'day')]);
        return;
      }

      onDatesChange([nextCheckIn, nextCheckOut]);
      return;
    }

    onDatesChange(null);
  };

  const handleDecreaseNights = () => {
    if (totalNights <= 1) return;
    onDatesChange([checkInDate, checkOutDate.subtract(1, 'day')]);
  };

  const handleIncreaseNights = () => {
    if (totalNights >= MAX_NIGHTS) return;
    onDatesChange([checkInDate, checkOutDate.add(1, 'day')]);
  };

  const renderGuestInput = (
    label: React.ReactNode,
    value: number,
    onChange: (val: number) => void,
    min: number,
    max: number,
    minAddonWidth: number
  ) => (
    <Space.Compact style={{ width: '100%' }} size="large">
      <InputNumber
        min={min}
        max={max}
        value={value}
        onChange={(v) => onChange(Number(v ?? min))}
        style={{ flex: 1, minWidth: 50 }}
        size="large"
      />
      <Space.Addon>
        <div
          style={{
            minWidth: isMobile ? minAddonWidth : '100%',
            textAlign: 'center',
            borderLeft: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
            fontSize: isCompactLabel ? token.fontSizeSM : token.fontSize,
          }}
        >
          {label} / {intl.formatMessage({ id: 'booking.room', defaultMessage: 'Room' })}
        </div>
      </Space.Addon>
    </Space.Compact>
  );

  const dateField = (
    <div style={{ width: '100%' }}>
      <RangePicker
        style={{ width: '100%' }}
        size="large"
        classNames={{
          popup: {
            root: 'responsive-calendar-popup',
          },
        }}
        value={[checkInDate, checkOutDate]}
        disabledDate={disabledDate}
        format={DATE_FORMAT}
        onChange={(vals) => handleRangeChange(vals as [Dayjs | null, Dayjs | null] | null)}
      />
    </div>
  );

  return (
    <Row gutter={[16, 16]} align="middle">
      {isMobile ? (
        <>
          <Col span={24}>
            {dateField}
          </Col>

          <Col span={24}>
            <NightsStepper
              value={totalNights}
              min={1}
              max={MAX_NIGHTS}
              onDecrease={handleDecreaseNights}
              onIncrease={handleIncreaseNights}
            />
          </Col>

          <Col span={24}>
            <Collapse
              ghost
              expandIcon={({ isActive }) => (
                <CaretRightOutlined rotate={isActive ? 90 : 0} />
              )}
              style={{
                backgroundColor: token.colorBgContainer,
                borderRadius: 16,
                border: `1px solid ${token.colorBorder}`
              }}
            >
              <Panel header={guestSummary} key="1">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {renderGuestInput(
                    intl.formatMessage({ id: 'booking.adults', defaultMessage: 'Adults' }),
                    adults, onAdultsChange, 1, 99, 130
                  )}
                  {renderGuestInput(
                    intl.formatMessage({
                      id: 'booking.children',
                      defaultMessage: 'Children',
                    }),
                    children, onChildrenChange, 0, 99, 130
                  )}
                  {renderGuestInput(
                    intl.formatMessage({ id: 'booking.infant', defaultMessage: 'Infant' }),
                    infants, onInfantsChange, 0, 99, 130
                  )}
                </div>
              </Panel>
            </Collapse>
          </Col>

          <Col span={24}>
            <Button
              type="primary"
              size="large"
              block
              icon={<SearchOutlined />}
              onClick={onTrigger}
              loading={loading}
            >
              {intl.formatMessage({
                id: 'booking.cta',
                defaultMessage: 'Check',
              })}
            </Button>
          </Col>
        </>
      ) : (
        <>
          <Col span={24} md={24} lg={24} xl={8} xxl={6}>
            {dateField}
          </Col>

          <Col span={24} md={12} lg={12} xl={4} xxl={3}>
            <NightsStepper
              value={totalNights}
              min={1}
              max={MAX_NIGHTS}
              onDecrease={handleDecreaseNights}
              onIncrease={handleIncreaseNights}
            />
          </Col>

          <Col span={24} md={12} lg={12} xl={4} xxl={4}>
            {renderGuestInput(
              intl.formatMessage({ id: 'booking.adults', defaultMessage: 'Adults' }),
              adults, onAdultsChange, 1, 99, 80
            )}
          </Col>

          <Col span={24} md={12} lg={12} xl={4} xxl={4}>
            {renderGuestInput(
              intl.formatMessage({ id: 'booking.children', defaultMessage: 'Children' }),
              children, onChildrenChange, 0, 99, 80
            )}
          </Col>

          <Col span={24} md={12} lg={12} xl={4} xxl={3}>
            {renderGuestInput(
              intl.formatMessage({ id: 'booking.infant', defaultMessage: 'Infant' }),
              infants, onInfantsChange, 0, 99, 100
            )}
          </Col>

          <Col span={24} md={24} lg={24} xl={24} xxl={4}>
            <Button
              type="primary"
              size="large"
              block
              icon={<SearchOutlined />}
              onClick={onTrigger}
              loading={loading}
            >
              {intl.formatMessage({
                id: 'booking.cta',
                defaultMessage: 'Check',
              })}
            </Button>
          </Col>
        </>
      )}
    </Row>
  );
};

export default BookingSearchFormVersion2;